import { isCorpTenant } from "@capone/common";
import { createSelector } from "@reduxjs/toolkit";
import { CustomerAccountRole, RewardsAccount, RewardsPrice } from "redmond";
import { config } from "../../../../api/config";
import { IStoreState } from "../../../../reducers/types";

export const getIsFirstLaunch = (state: IStoreState) =>
  state.hotelRewards.isFirstLaunch;

export const getAgentEmail = (state: IStoreState) =>
  state.hotelRewards.agentEmail;

export const getRewardsAccounts = (state: IStoreState) =>
  state.hotelRewards.rewardsAccounts;

export const getUserSelectedAccountReferenceId = (state: IStoreState) =>
  state.hotelRewards.selectedAccountReferenceId;

export const getFetchRewardsAccountsCallState = (state: IStoreState) =>
  state.hotelRewards.fetchRewardsAccountsCallState;

export const getRewardsAccountWithLargestValue = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount | undefined => {
    const sortedAccts = [...rewardsAccounts].sort(
      (prev, current) =>
        current.rewardsCashEquivalent.value - prev.rewardsCashEquivalent.value
    );
    return sortedAccts[0];
  }
);
export const getRewardsAccountWithLargestEarnForBanner = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount => rewardsAccounts[0]
);

export const getRewardsAccountForBanner = createSelector(
  getRewardsAccountWithLargestEarnForBanner,
  (highestEarnAccount): RewardsAccount | undefined => highestEarnAccount
);

export const getCardPaymentRewardsAccount = (state: IStoreState) => {
  const accountId = state.hotelBook.paymentMethodRewardsAccountId;
  const rewardsAccounts = getRewardsAccounts(state);
  return rewardsAccounts.find(
    (account) => account.accountReferenceId === accountId
  );
};

export const getEarnForHighestEarnRewardsAccount = createSelector(
  getRewardsAccountWithLargestEarnForBanner,
  (state: IStoreState) => state.hotelBook.earnValuesByRewardAcctId,
  (state: IStoreState) => state.hotelBook.priceQuote,
  (state: IStoreState) => state.hotelBook.rewardsPaymentInFiatCurrency,
  (state: IStoreState) => state.hotelBook.offerToApply,
  (state: IStoreState) => state.hotelBook.creditToApply,
  (state: IStoreState) => state.hotelBook.paymentMethodRewardsAccountId,
  (
    highestEarnRewardsAccount,
    earnValuesByRewardAcctId,
    priceQuote,
    rewardsPaymentInFiatCurrency,
    offerToApply,
    creditToApply,
    paymentMethodRewardsAccountId
  ): RewardsPrice | undefined => {
    const rewardsAccount = highestEarnRewardsAccount;

    if (!rewardsAccount) {
      return undefined;
    }

    if (rewardsAccount?.customerAccountRole !== CustomerAccountRole.Primary) {
      return undefined;
    }

    const tripTotal = priceQuote?.pricing?.tripTotal?.fiat?.value;

    if (!tripTotal) {
      const earnForAccount =
        earnValuesByRewardAcctId[rewardsAccount.accountReferenceId];

      if (!earnForAccount) {
        return undefined;
      }

      return {
        value: earnForAccount.amount,
        currency: rewardsAccount.rewardsBalance.currency,
        currencyDescription:
          rewardsAccount.rewardsBalance.currencyDescription || "miles",
      };
    }

    if (paymentMethodRewardsAccountId !== rewardsAccount.accountReferenceId) {
      return undefined;
    }

    const rewardsPayment = rewardsPaymentInFiatCurrency?.value || 0;
    const offerAmount = offerToApply?.amount?.amount || 0;
    const creditAmount = creditToApply?.amount?.amount || 0;

    const totalCreditsAndRewards = rewardsPayment + offerAmount + creditAmount;
    const creditCardPaymentRequired = Math.max(
      0,
      tripTotal - totalCreditsAndRewards
    );

    if (creditCardPaymentRequired <= 0) {
      return undefined;
    }

    const earnForAccount =
      earnValuesByRewardAcctId[rewardsAccount.accountReferenceId];

    if (!earnForAccount) {
      return undefined;
    }

    const earnAmount = earnForAccount.amount;

    return {
      value: earnAmount,
      currency: rewardsAccount.rewardsBalance.currency,
      currencyDescription:
        rewardsAccount.rewardsBalance.currencyDescription || "miles",
    };
  }
);

// the rewards account selection doesn't have a default state where none of the available options is selected
export const getDefaultSelectedAccountReferenceId = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string | null => {
    // return the id with largest earn
    if (rewardsAccounts.length > 0) {
      return rewardsAccounts[0].accountReferenceId;
    }

    return null;
  }
);

// this is the primary selector used outside the component
// either returns the user selected account id, or the default from above if not present
export const getSelectedAccountReferenceId = createSelector(
  getUserSelectedAccountReferenceId,
  getDefaultSelectedAccountReferenceId,
  (
    userSelectedAccountReferenceId,
    defaultSelectedAccountReferenceId
  ): string | null =>
    userSelectedAccountReferenceId ?? defaultSelectedAccountReferenceId
);

export const getSelectedAccountReferenceIdIfRedemptionEnabled = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, selectedAccountId) => {
    const account = accounts.find(
      (account) => account.accountReferenceId === selectedAccountId
    );

    return account?.allowRewardsRedemption ?? true ? selectedAccountId : null;
  }
);

export const getSelectedAccount = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (rewardsAccounts, referenceId): RewardsAccount | undefined =>
    rewardsAccounts.find(
      (account) => account.accountReferenceId === referenceId
    )
);

export const getSelectedAccountIndex = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, id) =>
    accounts.findIndex((account) => account.accountReferenceId === id)
);

export const getRewardsAccountsReferenceIds = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string[] =>
    rewardsAccounts.map((account) => account.accountReferenceId)
);

// Corporate Travel

export const getAllowRewardsRedemptionInAnyAccount = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): boolean =>
    rewardsAccounts.some((account) => account.allowRewardsRedemption ?? true)
);

export const getCorporateInfo = (state: IStoreState) =>
  state.hotelRewards.corporateInfo;

export const getAllowRewardsWithPolicy = createSelector(
  getAllowRewardsRedemptionInAnyAccount,
  (allowRewards): boolean => {
    if (isCorpTenant(config.TENANT)) {
      return allowRewards;
    }
    return true;
  }
);

export const getUserIsPrimaryCardHolder = createSelector(
  getRewardsAccounts,
  (rewardsAccounts) =>
    rewardsAccounts.some(
      (account) => account.customerAccountRole === CustomerAccountRole.Primary
    )
);
